import { Check, Table2 } from 'lucide-react';
import type { LearningTableMeasurement } from '../shared/desktop-api';
import { LearningTrialSummary } from './LearningTrialSummary';
import { learningTableTargetKey, learningTableTrialLabel } from './learning-attempts';

export function LearningTableProgress({
  measurements,
  failed,
  onRetry,
  onInspect,
}: {
  measurements: LearningTableMeasurement[];
  failed: boolean;
  onRetry: () => void;
  onInspect?: (targetKey: string) => void;
}) {
  if (!measurements.length && !failed) return null;
  return (
    <section className="learning-session-report" aria-label="Measured table improvements">
      <header>
        <Table2 size={20} />
        <div>
          <span className="eyebrow">Checked against your confirmed rows</span>
          <h2>Line item learning results</h2>
        </div>
      </header>
      {failed && (
        <div role="status">
          <strong>
            {measurements.length
              ? 'Could not refresh · showing last received table results'
              : 'Line item learning results could not be read'}
          </strong>{' '}
          <button type="button" className="quiet-button small" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}
      <div className="learning-field-results">
        {measurements.slice(0, 6).map((row) => (
          <article key={`${row.sessionId}:${row.layoutId}:${row.evidenceKey}:${row.recordedAt}`}>
            <header>
              <strong>
                {row.documentType} · {learningTableTrialLabel(row)}
              </strong>
              <span
                className={
                  row.ready || row.attempts?.some((attempt) => attempt.adopted)
                    ? 'learning-field-adopted'
                    : ''
                }
              >
                {row.ready ? (
                  <>
                    <Check size={15} /> Available for use
                  </>
                ) : row.attempts?.some((attempt) => attempt.adopted) ? (
                  <>
                    <Check size={15} /> Saved after this check
                  </>
                ) : row.checkStage === 'final-repair' && row.outcome === 'validated' ? (
                  'Improvement validated'
                ) : row.checkStage ? (
                  'Existing reading kept'
                ) : row.outcome === 'validated' ? (
                  'Passed · not currently available'
                ) : (
                  'Not applied'
                )}
              </span>
            </header>
            {row.judged > 0 ? (
              <>
                <div className="learning-field-comparison">
                  <span>Before</span>
                  <progress
                    aria-label={`${row.documentType} tables before`}
                    value={row.beforeRight}
                    max={row.judged}
                  />
                  <b>
                    {row.beforeRight}/{row.judged}
                  </b>
                </div>
                <div className="learning-field-comparison">
                  <span>New approach</span>
                  <progress
                    aria-label={`${row.documentType} tables candidate`}
                    value={row.afterRight}
                    max={row.judged}
                  />
                  <b>
                    {row.afterRight}/{row.judged}
                  </b>
                </div>
                <small>Measures all line items together on {row.judged} untouched documents</small>
                <small>
                  {row.regressions
                    ? `${row.regressions} previously correct tables lost — change not saved`
                    : 'No previously correct tables lost'}
                </small>
              </>
            ) : (
              <>
                <LearningTrialSummary
                  evidenceKey={row.evidenceKey}
                  attempts={row.attempts}
                  tables
                  training={row.checkStage === 'training'}
                />
                <small>
                  {row.checkStage === 'training'
                    ? `Checked ${row.documentsRead ?? 0} examples while developing changes. No proposed change improved enough to test further.`
                    : row.checkStage === 'search'
                      ? `Compared possible changes on ${row.documentsRead ?? 0} examples. No final result was accepted; your existing reading is unchanged.`
                      : row.outcome === 'evidence-changed'
                        ? 'Your setup or confirmations changed.'
                        : row.outcome === 'validation-already-reserved'
                          ? 'New confirmed documents are needed for a fresh final check.'
                          : row.outcome === 'interrupted'
                            ? 'Processing interrupted the final check before it finished. Its final documents were used, so a fresh final check needs new confirmed documents.'
                            : 'No final check: no approach qualified for further testing.'}
                </small>
              </>
            )}
            {row.ready && (
              <small>
                Available for matching tables. Actual use and speed are reported separately.
              </small>
            )}
            {!row.ready && row.attempts?.some((attempt) => attempt.adopted) && (
              <small>
                This records when the change was saved. It does not show whether it is currently
                available or in use.
              </small>
            )}
            {row.checkStage === 'final-repair' && (
              <small>Saved comparison result. Current use and speed are not measured here.</small>
            )}
            {row.checkStage === 'final-repair' && row.outcome === 'collateral-held' && (
              <small>
                Existing reading was kept because the change worsened another confirmed field.
              </small>
            )}
            {row.checkStage === 'final-repair' && row.outcome === 'validation-held' && (
              <small>The change did not pass every independent final check.</small>
            )}
            {onInspect && !!row.attempts?.length && (
              <button
                type="button"
                className="quiet-button small"
                onClick={() => onInspect(learningTableTargetKey(row))}
              >
                View approaches
              </button>
            )}
            <details>
              <summary>Measurement details</summary>
              <small>
                {new Date(row.recordedAt).toLocaleString()}
                <br />
                {row.checkStage === 'final-repair'
                  ? 'Independent final check'
                  : row.checkStage === 'training'
                    ? 'Early comparison against confirmed examples'
                    : row.checkStage === 'search'
                      ? 'Comparison before the independent final check'
                      : 'Matching table examples'}
                <br />
                {row.readerInvocations} reading passes
                <br />
                Exact ordered descriptions and numeric cells; not whole-job accuracy.
                {row.checkStage && (
                  <>
                    <br />
                    Tests numeric corrections, not overall production accuracy. Existing learned
                    reading routes are measured separately.
                  </>
                )}
                <br />
                {row.checkStage === 'training' || row.checkStage === 'search'
                  ? 'This was an early check. It did not produce a final accuracy result or a saved change.'
                  : 'Each comparison uses its own final document set.'}
              </small>
            </details>
          </article>
        ))}
      </div>
    </section>
  );
}
